import {authMiddleware} from '@/helpers/authMiddleware';
import {HOLD_DURATION_MINUTES} from '@/helpers/checkoutConfig';
import {createHoldExpirySchedule} from '@/helpers/holdExpiryScheduler.server';
import {calculateTakeHome} from '@/helpers/pricingCalculator';
import {prismaClient} from '@/helpers/prismaClient.server';
import {stripeClient} from '@/helpers/stripeClient.server';
import {getProfileName} from '@/utils/getProfileName';
import {createServerFn} from '@tanstack/react-start';
import {z} from 'zod';

/**
 * Creates a Stripe PaymentIntent and a HOLD registration for an event
 * category. Idempotent — returns an existing live hold if one exists
 * for the same user + event rather than creating a duplicate.
 */
export const createCheckoutSession = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator(
    (
      data: unknown
    ): {eventId: string; categoryId: string} => {
      const result = z
        .object({
          eventId: z.string().uuid(),
          categoryId: z.string().uuid(),
        })
        .safeParse(data);
      if (!result.success) {
        throw new Error('Invalid checkout input');
      }
      return result.data;
    }
  )
  .handler(async ({data, context}) => {
    const db = await prismaClient();
    const stripe = await stripeClient();
    const profileId = context.auth.profile.id;
    const now = new Date();

    // Check for existing registrations for this user + event
    const existingRegistrations = await db.registration.findMany({
      where: {
        profileId,
        eventId: data.eventId,
        status: {in: ['HOLD', 'PAID', 'APPROVED']},
      },
    });

    for (const reg of existingRegistrations) {
      if (reg.status === 'PAID' || reg.status === 'APPROVED') {
        throw new Error('Already registered for this event');
      }

      if (reg.status === 'HOLD') {
        // Stale HOLD — no Stripe PI, clean up and allow retry
        if (!reg.stripePaymentIntentId) {
          await db.registration.delete({where: {id: reg.id}});
          continue;
        }

        // Live HOLD with valid PI — return existing
        if (reg.holdExpiresAt && reg.holdExpiresAt > now) {
          return {
            registrationId: reg.id,
          };
        }

        // Expired HOLD with PI — clean up on retry (WAY-443 covers background sweep)
        if (!reg.holdExpiresAt || reg.holdExpiresAt <= now) {
          await stripe
            .paymentIntents.cancel(reg.stripePaymentIntentId)
            .catch(() => {});
          await db.registration.delete({where: {id: reg.id}});
          continue;
        }
      }
    }

    // Fetch category with prices and event with organizer
    const category = await db.category.findUnique({
      where: {id: data.categoryId},
      include: {
        prices: {orderBy: {endsAt: {sort: 'asc', nulls: 'last'}}},
      },
    });

    if (!category) {
      throw new Error('Category not found');
    }

    if (category.eventId !== data.eventId) {
      throw new Error('Category does not belong to this event');
    }

    if (!category.isOpen) {
      throw new Error('Category is not open for registration');
    }

    if (category.isSoldOut) {
      throw new Error('Category is sold out');
    }

    const event = await db.event.findUnique({
      where: {id: data.eventId},
      include: {organizer: true},
    });

    if (!event) {
      throw new Error('Event not found');
    }

    if (!event.organizer) {
      throw new Error('Event has no organizer');
    }

    if (
      !event.organizer.stripeConnectId ||
      !event.organizer.canAcceptPayments
    ) {
      throw new Error('Organizer cannot accept payments');
    }

    // Find active price: first price where endsAt is null or in the future
    const activePrice = category.prices.find(
      (p) => p.endsAt === null || p.endsAt > now
    );

    if (!activePrice) {
      throw new Error('No active price found for this category');
    }

    if (activePrice.amount <= 0) {
      throw new Error('Category does not require payment');
    }

    // Calculate fees
    const fees = calculateTakeHome(
      activePrice.amount,
      event.organizer.commissionBps
    );
    // Organizer pays processing: the platform's Stripe fee is withheld
    // alongside the commission so payouts match the take-home breakdown
    const applicationFeeAmount = fees.stripeFeeCents + fees.wayraFeeCents;

    // Capacity note: spotsRemaining is only used for externally hosted events
    // where we don't control registration (no payments). For native events,
    // available spots are computed dynamically by counting HOLD + PAID
    // registrations against maxParticipants — no atomic decrement needed here.
    const holdExpiresAt = new Date(
      now.getTime() + HOLD_DURATION_MINUTES * 60 * 1000
    );

    const registration = await db.registration.create({
      data: {
        profileId,
        eventId: data.eventId,
        categoryId: data.categoryId,
        routeId: category.routeId ?? null,
        status: 'HOLD',
        holdExpiresAt,
        amountPaidCents: activePrice.amount,
        currency: activePrice.currency.toLowerCase(),
      },
    });

    // Create Stripe PaymentIntent (outside transaction — Stripe is not transactional)
    let paymentIntentId: string | null = null;
    try {
      const paymentIntent = await stripe.paymentIntents.create({
        amount: activePrice.amount,
        currency: activePrice.currency.toLowerCase(),
        payment_method_types: ['card'],
        transfer_data: {
          destination: event.organizer.stripeConnectId,
        },
        application_fee_amount: applicationFeeAmount,
        metadata: {
          registrationId: registration.id,
          eventId: data.eventId,
          categoryId: data.categoryId,
        },
      });
      paymentIntentId = paymentIntent.id;

      // Store the PI ID on the registration
      await db.registration.update({
        where: {id: registration.id},
        data: {
          stripePaymentIntentId: paymentIntent.id,
        },
      });

      // Schedule background expiry — best-effort, awaited to ensure
      // the call completes before Lambda freezes the event loop
      await createHoldExpirySchedule({
        registrationId: registration.id,
        fireAt: holdExpiresAt,
      }).catch((err) => {
        console.error(
          '[createCheckoutSession] Failed to schedule hold expiry:',
          err,
        );
      });

      return {
        registrationId: registration.id,
      };
    } catch (error) {
      // Cancel the Stripe PI if it was created
      if (paymentIntentId) {
        await stripe.paymentIntents
          .cancel(paymentIntentId)
          .catch(() => {});
      }
      // Clean up the HOLD registration
      await db.registration.delete({where: {id: registration.id}});
      throw error;
    }
  });

/**
 * Returns the PaymentIntent client secret and checkout page display
 * data for a registration. Called by the checkout route loader.
 */
export const getCheckoutPageData = createServerFn({method: 'GET'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): {registrationId: string} => {
    const result = z
      .object({registrationId: z.string().uuid()})
      .safeParse(data);
    if (!result.success) {
      throw new Error('Invalid registration ID');
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    const db = await prismaClient();
    const stripe = await stripeClient();
    const {profile} = context.auth;

    const registration = await db.registration.findUnique({
      where: {id: data.registrationId},
      include: {
        event: {
          select: {
            id: true,
            name: true,
            startDate: true,
            heroUrl: true,
            _count: {select: {routes: true}},
          },
        },
        category: {
          select: {id: true, name: true, startTime: true},
        },
        route: {
          select: {name: true, _count: {select: {categories: true}}},
        },
      },
    });

    if (!registration) {
      throw new Error('Registration not found');
    }

    if (registration.profileId !== profile.id) {
      throw new Error('Not authorized to view this registration');
    }

    const billingDetails = {
      // Null when the profile has no name; the PaymentElement then collects it
      name: getProfileName(profile, '') || null,
      email: profile.email,
    };

    const routeCount = registration.event._count.routes;
    const fieldCount = registration.route?._count.categories ?? 0;

    const sharedRegistrationDetails = {
      billingDetails,
      event: registration.event,
      category: registration.category,
      amountCents: registration.amountPaidCents,
      routeName: registration.route?.name ?? null,
      currency: registration.currency,
      routeCount,
      fieldCount,
    };

    if (registration.status === 'PAID') {
      return {
        ...sharedRegistrationDetails,
        status: 'paid' as const,
        clientSecret: null,
      };
    }

    if (registration.status === 'EXPIRED') {
      return {
        ...sharedRegistrationDetails,
        status: 'expired' as const,
        clientSecret: null,
      };
    }

    if (!registration.stripePaymentIntentId || registration.status !== 'HOLD') {
      return {
        ...sharedRegistrationDetails,
        status: 'invalid' as const,
        clientSecret: null,
      };
    }

    if (
      registration.holdExpiresAt &&
      registration.holdExpiresAt <= new Date()
    ) {
      return {
        ...sharedRegistrationDetails,
        status: 'expired' as const,
        clientSecret: null,
      };
    }

    // Retrieve the PI client secret server-side
    const pi = await stripe.paymentIntents.retrieve(
      registration.stripePaymentIntentId
    );

    return {
      ...sharedRegistrationDetails,
      status: 'hold' as const,
      clientSecret: pi.client_secret,
      holdExpiresAt: registration.holdExpiresAt,
    };
  });
